import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InsightsIcon from '@mui/icons-material/Insights';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';

const InterviewDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const interviews = useAppSelector((state) => state.interviews.items);
  const insights = useAppSelector((state) => state.insights.items);

  const interview = interviews.find((item) => item.id === id);
  const relatedInsights = insights.filter((insight) => insight.interviewId === id);

  if (!interview) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="error">
          インタビューが見つかりませんでした
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          一覧に戻る
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/"
          sx={{
            color: 'white',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'rgba(255, 255, 255, 0.8)'
            }
          }}
        >
          インサイト一覧
        </Link>
        <Typography sx={{ color: 'white' }}>
          インタビュー詳細
        </Typography>
      </Breadcrumbs>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        戻る
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          インタビュー詳細
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">実施日</Typography>
            </Box>
            <Typography variant="body1">
              {new Date(interview.date).toLocaleDateString('ja-JP', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long',
              })}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">インタビュアー</Typography>
            </Box>
            <Typography variant="body1">{interview.interviewer}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'secondary.main' }} />
              <Typography variant="h6">インタビュイー</Typography>
            </Box>
            <Typography variant="body1">{interview.interviewee}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          インタビューサマリー
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Paper sx={{
            p: 3,
            mb: 2,
            bgcolor: 'rgba(63, 81, 181, 0.05)',
            borderLeft: '4px solid #3f51b5'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              🎯 主要な課題・ニーズ
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
              • 既存のCRMシステムでは営業進捗の可視化が困難<br/>
              • 顧客データが部署間で分散しており、一元管理ができていない<br/>
              • 営業メンバーのスキルレベルにばらつきがあり、標準化が必要<br/>
              • 月次報告作成に多大な時間を要している（現在約8時間/月）
            </Typography>
          </Paper>

          <Paper sx={{
            p: 3,
            mb: 2,
            bgcolor: 'rgba(245, 0, 87, 0.05)',
            borderLeft: '4px solid #f50057'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'secondary.main' }}>
              💡 提案・解決策への反応
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
              • ダッシュボード機能には高い関心を示した<br/>
              • 自動レポート生成機能は「ぜひ導入したい」と積極的<br/>
              • モバイル対応について、外出先での利用シーンを具体的に説明<br/>
              • 導入時期は来年度予算に組み込む予定（4月開始希望）
            </Typography>
          </Paper>

          <Paper sx={{
            p: 3,
            mb: 2,
            bgcolor: 'rgba(76, 175, 80, 0.05)',
            borderLeft: '4px solid #4caf50'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#4caf50' }}>
              📊 具体的な数値・目標
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
              • 営業効率20%向上を目標<br/>
              • 月次報告作成時間を現在の8時間から2時間に短縮<br/>
              • 顧客満足度スコア向上（現在7.2 → 目標8.5）<br/>
              • 予算規模：年間300万円程度
            </Typography>
          </Paper>

          <Paper sx={{
            p: 3,
            bgcolor: 'rgba(255, 152, 0, 0.05)',
            borderLeft: '4px solid #ff9800'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#ff9800' }}>
              ⚠️ 懸念点・検討事項
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              • 既存システムとの連携可能性について確認が必要<br/>
              • 従業員への操作研修の実施方法・期間<br/>
              • データ移行時のダウンタイム最小化<br/>
              • セキュリティ要件（特に個人情報保護）の詳細確認
            </Typography>
          </Paper>
        </Box>

        <Divider sx={{ my: 3 }} />

        {relatedInsights.length > 0 && (
          <>
            <Typography variant="h6" gutterBottom>
              このインタビューから抽出されたインサイト ({relatedInsights.length}件)
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {relatedInsights.map((insight) => (
                <Grid item xs={12} md={6} key={insight.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <InsightsIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" component="h3">
                          {insight.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {insight.summary}
                      </Typography>
                      <Button
                        variant="outlined"
                        component={RouterLink}
                        to={`/insights/${insight.id}`}
                        startIcon={<InsightsIcon />}
                        size="small"
                      >
                        インサイト詳細を見る
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default InterviewDetailPage;
